/**
 * LLM Prompt Template Service
 * Manages system templates and user-customized templates in Firebase
 * 
 * Architecture:
 * - llm_prompt_templates/ : System-wide templates (admin-managed)
 * - user_llm_templates/{userId}/ : User's custom/forked templates
 * 
 * Fallback: If Firebase is unavailable, uses localStorage
 */

import { RealtimeDBService } from '../../firebase/unifiedDataService';
import { getFirebasePaths } from '../../firebase/schema';
import { PROMPT_TEMPLATES, TEMPLATE_CATEGORIES } from './promptTemplates';
import { Logger } from '../../utils/logger';
import { templateStorage } from '../../services/storageService';

const log = Logger.create('templateService');

// ============================================================================
// CONSTANTS
// ============================================================================

const LOCAL_STORAGE_KEY = 'llm_templates_cache';
const LOCAL_USER_TEMPLATES_KEY = 'llm_user_templates';

// ============================================================================
// SYSTEM TEMPLATES (Read-only for users, Admin-managed)
// ============================================================================

/**
 * Initialize system templates in Firebase (Admin function)
 * Call this once to populate Firebase with default templates from promptTemplates.js
 */
export async function initializeSystemTemplates() {
  try {
    for (const [id, template] of Object.entries(PROMPT_TEMPLATES)) {
      await RealtimeDBService.set(`llm_prompt_templates/${id}`, {
        ...template,
        id,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        isActive: true
      });
    }
    
    log.success('System templates initialized in Firebase');
    return { success: true };
  } catch (error) {
    log.error('Error initializing system templates:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get all system templates
 * Falls back to hardcoded templates if Firebase unavailable
 */
export async function getSystemTemplates() {
  try {
    const templates = await RealtimeDBService.get('llm_prompt_templates');
    
    if (templates) {
      // Cache in localStorage for offline access
      templateStorage.set('system_cache', templates);
      return Object.values(templates).filter(t => t.isActive !== false);
    }
    
    // Return cached or hardcoded templates
    return getFallbackSystemTemplates();
  } catch (error) {
    log.error('Error fetching system templates:', error);
    return getFallbackSystemTemplates();
  }
}

/**
 * Get a single system template by ID
 */
export async function getSystemTemplate(templateId) {
  try {
    const template = await RealtimeDBService.get(`llm_prompt_templates/${templateId}`);
    
    if (template) {
      return template;
    }
    
    // Fallback to hardcoded
    return PROMPT_TEMPLATES[templateId] || null;
  } catch (error) {
    log.error('Error fetching system template:', error);
    return PROMPT_TEMPLATES[templateId] || null;
  }
}

/**
 * Fallback: Get templates from localStorage cache or hardcoded
 */
function getFallbackSystemTemplates() {
  try {
    const cached = templateStorage.get('system_cache');
    if (cached) {
      return Object.values(cached);
    }
  } catch {
    // Ignore parse errors
  }
  
  // Ultimate fallback: hardcoded templates
  return Object.values(PROMPT_TEMPLATES);
}

// ============================================================================
// USER TEMPLATES (User-specific customizations)
// ============================================================================

/**
 * Get all user-customized templates
 */
export async function getUserTemplates(userId) {
  if (!userId) return [];
  
  try {
    const paths = getFirebasePaths(userId);
    const templates = await RealtimeDBService.get(paths.userLlmTemplates());
    
    if (templates) {
      // Cache locally
      templateStorage.set(`user_${userId}`, templates);
      return Object.values(templates);
    }
    
    return getFallbackUserTemplates(userId);
  } catch (error) {
    log.error('Error fetching user templates:', error);
    return getFallbackUserTemplates(userId);
  }
}

/**
 * Get a single user template by ID
 */
export async function getUserTemplate(userId, templateId) {
  if (!userId) return null;
  
  try {
    const paths = getFirebasePaths(userId);
    const template = await RealtimeDBService.get(paths.userLlmTemplate(templateId));
    
    return template || null;
  } catch (error) {
    log.error('Error fetching user template:', error);
    return null;
  }
}

/**
 * Save a user template (create or update)
 */
export async function saveUserTemplate(userId, template) {
  if (!userId) {
    return { success: false, error: 'User not authenticated' };
  }
  
  try {
    const paths = getFirebasePaths(userId);
    const templateId = template.id || `custom_${Date.now()}`;
    
    const templateData = {
      ...template,
      id: templateId,
      category: template.category || 'custom',
      updatedAt: Date.now(),
      createdAt: template.createdAt || Date.now()
    };
    
    await RealtimeDBService.set(paths.userLlmTemplate(templateId), templateData);
    
    // Update local cache
    updateLocalUserTemplateCache(userId, templateId, templateData);
    
    return { success: true, id: templateId };
  } catch (error) {
    log.error('Error saving user template:', error);
    
    // Fallback: Save to localStorage
    return saveUserTemplateLocally(userId, template);
  }
}

/**
 * Delete a user template
 */
export async function deleteUserTemplate(userId, templateId) {
  if (!userId) return false;
  
  try {
    const paths = getFirebasePaths(userId);
    await RealtimeDBService.remove(paths.userLlmTemplate(templateId));
    
    // Remove from local cache
    removeFromLocalUserTemplateCache(userId, templateId);
    
    return true;
  } catch (error) {
    log.error('Error deleting user template:', error);
    // Try local deletion
    removeFromLocalUserTemplateCache(userId, templateId);
    return false;
  }
}

/**
 * Fork a system template to user's collection
 * Creates a copy that the user can customize
 */
export async function forkSystemTemplate(userId, systemTemplateId, customizations = {}) {
  const systemTemplate = await getSystemTemplate(systemTemplateId);
  
  if (!systemTemplate) {
    return { success: false, error: 'Template not found' };
  }
  
  const forkedTemplate = {
    ...systemTemplate,
    id: undefined, // Will be generated
    name: customizations.name || `${systemTemplate.name} (Angepasst)`,
    description: customizations.description || systemTemplate.description,
    systemPrompt: customizations.systemPrompt || systemTemplate.systemPrompt,
    exampleTags: customizations.exampleTags || systemTemplate.exampleTags,
    icon: customizations.icon || systemTemplate.icon,
    baseTemplateId: systemTemplateId, // Track the source
    category: systemTemplate.category,
    isForked: true
  };
  
  return saveUserTemplate(userId, forkedTemplate);
}

// ============================================================================
// COMBINED ACCESS (System + User templates)
// ============================================================================

/**
 * Get all templates available to a user (system + custom)
 */
export async function getAllTemplatesForUser(userId) {
  const [systemTemplates, userTemplates] = await Promise.all([
    getSystemTemplates(),
    getUserTemplates(userId)
  ]);
  
  // Mark templates with their source
  const system = systemTemplates.map(t => ({ ...t, isSystem: true, isCustom: false }));
  const user = userTemplates.map(t => ({ ...t, isSystem: false, isCustom: true }));
  
  return [...system, ...user];
}

/**
 * Get a template by ID (checks both system and user templates)
 */
export async function getTemplateForUser(userId, templateId) {
  // First check user templates (custom takes priority)
  const userTemplate = await getUserTemplate(userId, templateId);
  if (userTemplate) {
    return { ...userTemplate, isCustom: true, isSystem: false };
  }
  
  // Then check system templates
  const systemTemplate = await getSystemTemplate(templateId);
  if (systemTemplate) {
    return { ...systemTemplate, isCustom: false, isSystem: true };
  }
  
  return null;
}

/**
 * Get categories with templates for a user
 */
export async function getCategoriesWithTemplatesForUser(userId) {
  const allTemplates = await getAllTemplatesForUser(userId);
  
  // Group by category
  const categories = Object.values(TEMPLATE_CATEGORIES).map(category => ({
    ...category,
    templates: allTemplates.filter(t => t.category === category.id)
  }));
  
  // Add custom category if user has custom templates
  const customTemplates = allTemplates.filter(t => t.category === 'custom' || t.isCustom);
  if (customTemplates.length > 0) {
    categories.push({
      id: 'custom',
      name: 'Eigene Vorlagen',
      icon: '⚙️',
      description: 'Selbst erstellte und angepasste Templates',
      templates: customTemplates
    });
  }
  
  return categories;
}

// ============================================================================
// REALTIME SUBSCRIPTIONS
// ============================================================================

/**
 * Subscribe to user templates changes
 */
export function subscribeToUserTemplates(userId, callback) {
  if (!userId) return () => {};
  
  const paths = getFirebasePaths(userId);
  
  return RealtimeDBService.subscribe(paths.userLlmTemplates(), (templates) => {
    const templatesArray = templates ? Object.values(templates) : [];
    callback(templatesArray);
  });
}

// ============================================================================
// LOCAL STORAGE HELPERS (Fallback)
// ============================================================================

function getFallbackUserTemplates(userId) {
  try {
    const cached = templateStorage.get(`user_${userId}`);
    if (cached) {
      return Object.values(cached);
    }
  } catch {
    // Ignore
  }
  return [];
}

function updateLocalUserTemplateCache(userId, templateId, templateData) {
  try {
    const templates = templateStorage.get(`user_${userId}`, {});
    templates[templateId] = templateData;
    templateStorage.set(`user_${userId}`, templates);
  } catch {
    // Ignore
  }
}

function removeFromLocalUserTemplateCache(userId, templateId) {
  try {
    const templates = templateStorage.get(`user_${userId}`);
    if (templates) {
      delete templates[templateId];
      templateStorage.set(`user_${userId}`, templates);
    }
  } catch {
    // Ignore
  }
}

function saveUserTemplateLocally(userId, template) {
  try {
    const templateId = template.id || `local_${Date.now()}`;
    const templateData = {
      ...template,
      id: templateId,
      category: template.category || 'custom',
      updatedAt: Date.now(),
      createdAt: template.createdAt || Date.now(),
      isLocalOnly: true
    };
    
    updateLocalUserTemplateCache(userId, templateId, templateData);
    return { success: true, id: templateId, isLocalOnly: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// ============================================================================
// EXPORT DEFAULT FUNCTIONS
// ============================================================================

export default {
  // System templates
  initializeSystemTemplates,
  getSystemTemplates,
  getSystemTemplate,
  
  // User templates
  getUserTemplates,
  getUserTemplate,
  saveUserTemplate,
  deleteUserTemplate,
  forkSystemTemplate,
  
  // Combined
  getAllTemplatesForUser,
  getTemplateForUser,
  getCategoriesWithTemplatesForUser,
  
  // Subscriptions
  subscribeToUserTemplates
};
